import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { SubmitButton, Overlay } from "@components";
import { Box, TextField, MenuItem } from "@mui/material";
import type { FormAction } from "features/users/types";
import type { Language } from "features/languages/types";
import { languagesService } from "features/languages/services/languages-services";
import { getCharacterTypesList } from "features/languages/utils";
import { useNotification } from "@hooks/useNotifications";
import { formRowStyle, formStyle } from "./styles";

const validationSchema = yup.object({
  isoCode: yup.string().required("Field is required").max(10, "ISO codes can't be longer than 10 characters."),
  name: yup.string().required("Name is required"),
});

export const LanguageForm = ({
  action,
  language,
  hasEditAccess,
}: {
  action?: FormAction;
  hasEditAccess: boolean;
  language: Language;
}) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [isUpdating, setIsUpdating] = useState(false);
  const characterTypes = useMemo(() => getCharacterTypesList(), []);

  const isCreate = action === "create";
  const isEdit = action === "edit";
  const isRead = action === "read";
  const isInputDisabled = isUpdating || !hasEditAccess || isRead;

  const formik = useFormik({
    initialValues: {
      isoCode: language.id ?? "",
      name: language.name,
      characterType: language.characterType,
    },
    validationSchema,
    onSubmit: async formValue => {
      setIsUpdating(true);
      try {
        // Check if language with this ISO code already exists
        if (isCreate) {
          let isLanguageExists = false;
          try {
            const response = await languagesService.getLanguage(formValue.isoCode);
            if (response?.id) {
              isLanguageExists = true;
            }
          } catch {
            // Do nothing
          }

          if (isLanguageExists) {
            showError("Language with this ISO code already exists.");
            setIsUpdating(false);
            return;
          }
        }

        const response = await languagesService.saveLanguage(formValue);

        if (response?.id) {
          showSuccess(isCreate ? "Language created." : "Language updated.");
          navigate(-1);
        }
      } catch (error) {
        showError("Language update failed. Please try again or contact support.");
        console.error(error);
      }
      setIsUpdating(false);
    },
  });

  return (
    <Box sx={formStyle}>
      <Overlay isVisible={isUpdating} />
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled || isEdit}
            required
            id="isoCode"
            label="ISO code"
            value={formik.values.isoCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.isoCode && Boolean(formik.errors.isoCode)}
            helperText={formik.touched.isoCode && formik.errors.isoCode}
          />
        </Box>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled}
            required
            id="name"
            label="Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
        </Box>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled}
            id="characterType"
            name="characterType"
            select
            label="Character type"
            value={formik.values.characterType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.characterType && Boolean(formik.errors.characterType)}
            helperText={formik.touched.characterType && formik.errors.characterType}
          >
            {characterTypes.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {hasEditAccess ? (
          <SubmitButton variant="contained" isProcessing={isUpdating} onClick={() => {}}>
            {(isCreate && "Create") || (isEdit && "Save")}
          </SubmitButton>
        ) : null}
      </Box>
    </Box>
  );
};
